import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppButton, Box, ShimmerBox, Text } from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import { ValuesMap } from '@/common/types';
import { useParseEmailTemplate } from '@/modules/core';

import UnableToDisplayPreview from './UnableToDisplayPreview';

const Gradient = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 34px;
  background: ${({
    theme,
  }) => `linear-gradient(0deg, ${theme.colors.canvas} 13.8%, rgba(255, 255, 255, 0.00) 84.48%);
Linear Gradient
`};
`;

interface Props {
  testId: string;
  htmlContent?: string;
  isHTMLContentLoading?: boolean;
  htmlVariables?: ValuesMap<string | number | boolean>;
  isHTMLContentError?: boolean;
  onRefetchHTMLContent?: () => void;
}

const TABLE_WIDTH = 620;
const GAP = 96;

const ResendListItemEmailPreview: React.FC<Props> = ({
  htmlContent = '',
  isHTMLContentLoading,
  htmlVariables,
  isHTMLContentError,
  onRefetchHTMLContent,
  testId,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [isShowMore, setShowMore] = useState(false);

  const [scale, setScale] = useState<number>(1);

  const handleToggleShowMore = useCallback(() => {
    setShowMore(!isShowMore);
  }, [isShowMore]);

  useEffect(() => {
    if (isHTMLContentLoading) {
      return;
    }

    const scaleTable = () => {
      if (wrapperRef.current) {
        const containerWidth = wrapperRef.current.clientWidth;
        const scaleFactor = (containerWidth - GAP) / TABLE_WIDTH;
        setScale(scaleFactor);
      }
    };

    scaleTable();
    window.addEventListener('resize', scaleTable);
    return () => window.removeEventListener('resize', scaleTable);
  }, [isHTMLContentLoading]);

  const handleParseHTML = useParseEmailTemplate();

  const formattedVariables = useMemo(() => {
    return _.mapKeys(htmlVariables, (value, key) => {
      return _.toLower(key);
    });
  }, [htmlVariables]);

  const parsedHTML = useMemo(() => {
    return handleParseHTML(htmlContent, formattedVariables, {
      paddingTop: '0px',
      transform: `scale(${scale})`,
      transformOrigin: 'top center',
    });
  }, [formattedVariables, handleParseHTML, htmlContent, scale]);

  const content = useMemo(() => {
    if (isHTMLContentLoading) {
      return (
        <ShimmerBox data-testid="html-preview_loader"  margin={{ top: 'spacing24' }} height='134px' />
      );
    }
    
    if (isHTMLContentError) {
      return (
        <UnableToDisplayPreview onRefetch={onRefetchHTMLContent} />
      );
    }

    return (
      <Box
        ref={wrapperRef}
        align="center"
        justify={isShowMore ? 'center' : 'start'}
        background="border1"
        width="100%"
        margin={{ top: 'l' }}
        pad={{ top: 'l' }}
        style={{
          position: 'relative',
          overflow: isShowMore ? 'auto' : 'hidden',
          maxHeight: isShowMore ? undefined : '134px',
        }}
      >
        {parsedHTML}

        {!isShowMore && <Gradient />}
      </Box>
    );
  }, [isHTMLContentLoading, isHTMLContentError, onRefetchHTMLContent, isShowMore, parsedHTML]);

  return (
    <Box data-testid={testId} direction="column">
      <Box direction="row" align="center" justify="between">
        <Text size="xxlarge" weight={400}>
          Sent message
        </Text>
        <AppButton testId={testId} buttonType="secondary" width="168px" onClick={handleToggleShowMore}>
          {isShowMore ? 'Hide Full View' : 'Show Full View'}
        </AppButton>
      </Box>
      {content}
    </Box>
  );
};

export default ResendListItemEmailPreview;
